import { useState, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

export function Tour({ steps, open, onClose }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  useEffect(() => { if (open) setIdx(0); }, [open]);

  const step = open ? steps[idx] : null;

  useEffect(() => {
    if (open && step?.route) navigate(step.route);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx]);

  useLayoutEffect(() => {
    if (!open || !step) return;
    const measure = () => {
      if (!step.target) { setRect(null); return; }
      const el = document.querySelector(step.target);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    const t = setTimeout(measure, 380);
    window.addEventListener("resize", measure);
    return () => { clearTimeout(t); window.removeEventListener("resize", measure); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !step) return null;

  const last = idx === steps.length - 1;
  const next = () => (last ? onClose() : setIdx((i) => i + 1));
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  let tipStyle;
  if (rect) {
    const top = Math.max(16, Math.min(rect.top, window.innerHeight - 280));
    tipStyle = { top, left: rect.right + 20 };
  } else {
    tipStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  }

  return createPortal(
    <div className="fixed inset-0 z-[100]">
      {rect ? (
        <>
          <div style={{ position: "fixed", inset: 0 }} />
          <div
            style={{
              position: "fixed", top: rect.top - 6, left: rect.left - 6,
              width: rect.width + 12, height: rect.height + 12,
              boxShadow: "0 0 0 9999px rgba(5,5,5,0.82)",
              border: "1.5px solid #F23F2C", borderRadius: 2, pointerEvents: "none",
            }}
          />
        </>
      ) : (
        <div style={{ position: "fixed", inset: 0, background: "rgba(5,5,5,0.86)" }} />
      )}

      <motion.div
        key={idx}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed z-[101] w-80 border border-border bg-background p-5 shadow-2xl"
        style={tipStyle}
        data-testid="tour-tooltip"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="font-mono text-[10px] tracking-widest text-primary flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> DEMO · {idx + 1}/{steps.length}
          </span>
          <button onClick={onClose} data-testid="tour-close" className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-heading text-lg font-bold tracking-tight">{step.title}</h3>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{step.body}</p>
        <div className="flex items-center justify-between mt-5">
          <button
            onClick={prev}
            disabled={idx === 0}
            data-testid="tour-prev"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <button
            onClick={next}
            data-testid="tour-next"
            className="inline-flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-xs px-4 py-2 transition-colors"
          >
            {last ? "Finish" : "Next"} {!last && <ArrowRight className="h-3.5 w-3.5" />}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
